import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Text,
  HStack,
  VStack,
  Card,
  CardBody,
  Link,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { AppContext } from './AppContext';

const USNComprehensiveDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCases = async () => {
      if (!accessToken) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await callApi('/usn/cases', accessToken, {
          method: 'GET'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            setCases(data.cases || []);
          } else {
            setError(data.message || 'Failed to fetch cases.');
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.message || `Server error: ${response.status}`);
        }
      } catch (err) {
        console.error('Error fetching USN cases:', err);
        setError('An error occurred while fetching cases.');
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [accessToken]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box maxW="container.xl" mx="auto" py={8} px={4}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Heading size="lg" color="brand.700">USN Comprehensive Dashboard</Heading>
              <Text color="gray.600">Overview of all cases processed through the USN Comprehensive IONM pipeline.</Text>
            </VStack>
          </HStack>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Card shadow="sm" borderRadius="lg">
            <CardBody>
              {loading ? (
                <VStack py={10}>
                  <Spinner size="xl" color="brand.500" />
                  <Text mt={4} color="gray.500">Loading cases...</Text>
                </VStack>
              ) : cases.length === 0 ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  No USN cases found.
                </Alert>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" size="md">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>External Case ID</Th>
                        <Th>Claim Type</Th>
                        <Th>Service Date</Th>
                        <Th>Payer Type</Th>
                        <Th>Final Codes</Th>
                        <Th isNumeric>Warnings</Th>
                        <Th>Processed At</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {cases.map((c, index) => (
                        <Tr key={`${c.internal_case_id}-${c.claim_type}-${index}`} _hover={{ bg: 'gray.50' }}>
                          <Td fontWeight="medium">
                            <Link 
                              href={`/ionm-strict-analysis/${c.external_case_id}`} 
                              isExternal 
                              color="brand.800" 
                              fontWeight="bold"
                            >
                              {c.external_case_id} <ExternalLinkIcon mx="2px" />
                            </Link>
                          </Td>
                          <Td>
                            <Badge colorScheme={c.claim_type === 'pro' ? 'blue' : 'purple'}>
                              {c.claim_type?.toUpperCase() || 'PRO'}
                            </Badge>
                          </Td>
                          <Td>{c.service_date || 'N/A'}</Td>
                          <Td>
                            <Badge colorScheme={c.payer_type === 'Medicare' ? 'blue' : 'purple'}>
                              {c.payer_type || 'N/A'}
                            </Badge>
                          </Td>
                          <Td>
                            <HStack wrap="wrap" spacing={1}>
                              {c.final_codes && c.final_codes.length > 0 ? (
                                c.final_codes.map((code, idx) => (
                                  <Badge key={idx} colorScheme="green" variant="solid">
                                    {code}
                                  </Badge>
                                ))
                              ) : (
                                <Text fontSize="xs" color="red.400">None</Text>
                              )}
                            </HStack>
                          </Td>
                          <Td isNumeric>
                            {c.warnings_count > 0 ? (
                              <Badge colorScheme="orange">{c.warnings_count}</Badge>
                            ) : (
                              <Text color="gray.500">0</Text>
                            )}
                          </Td>
                          <Td fontSize="sm" color="gray.600">{formatDate(c.created_at)}</Td>
                          <Td>
                            <Button
                              size="sm"
                              colorScheme="brand"
                              variant="outline"
                              rightIcon={<ExternalLinkIcon />}
                              onClick={() => window.open(`/ionm-strict-analysis/${c.external_case_id}`, '_blank')}
                            >
                              View
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </Box>
  );
};

export default USNComprehensiveDashboard;